import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, forkJoin } from 'rxjs';
import { toString as luceneToString, RangeExpression, FieldExpression } from 'lucene';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { OrderBy, OrderByDirection } from '../../../shared/utils/constant';
import { BarData } from '../../../shared/models/bar-data';
import { BatchesService } from '../../services/batches.service';
import { Batch } from '../../models/batch';
import { TranslationService } from '../../../shared/services/translation.service';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { BarsComponent } from '../../../shared/components/charts/bar-chart/bars.component';
import { DatacenterDefaultConfiguration } from '../../../shared/datacenter/datacenter-default-configuration';
import { DatacenterFilterType } from '../../../shared/models/datacenter-filters';
import { UserService } from '../../../shared/services/user.service';

@Component({
	selector: 'copo-time-in-stock',
	templateUrl: './time-in-stock.component.html',
	styleUrl: './time-in-stock.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	animations: pageTransitions,

	imports: [TranslateModule, NgIf, BarsComponent, LoadingSpinnerComponent, MatButtonModule, MatIconModule]
})
export class TimeInStockComponent implements OnDestroy, OnInit {
	batches?: Batch[];
	orderSub?: Subscription;
	isLoading = true;
	totalOfWeight: number[] = [];
	timeInStockGraphConfig: BarData[];

	private readonly previousState: Record<string, unknown> | undefined;
	private readonly hasEcallOff: boolean;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly batchService: BatchesService,
		private readonly router: Router,
		private readonly translationService: TranslationService,
		private readonly defaultConfiguration: DatacenterDefaultConfiguration,
		private readonly userService: UserService
	) {
		this.hasEcallOff = this.userService.user.hasService('PCALL');
		this.timeInStockGraphConfig = [
			{
				title: this.translationService.getTR('datacenter.graph.first-bar-label'),
				value: 0,
				barDataColor: 'primary',
				bold: false
			},
			{
				title: this.translationService.getTR('datacenter.graph.second-bar-label'),
				value: 0,
				barDataColor: 'accent',
				bold: true
			},
			{
				title: this.translationService.getTR('datacenter.graph.third-bar-label'),
				value: 0,
				barDataColor: 'accent',
				bold: true
			},
			{
				title: this.translationService.getTR('datacenter.graph.fourth-bar-label'),
				value: 0,
				barDataColor: 'accent',
				bold: true
			}
		];
	}

	ngOnInit() {
		this.orderSub = forkJoin([
			this.getBatchWithDaysInstock('daysInStock', '0', '30'),
			this.getBatchWithDaysInstock('daysInStock', '31', '60'),
			this.getBatchWithDaysInstock('daysInStock', '61', '90'),
			this.getBatchWithDaysInstock('daysInStock', '90', '*')
		]).subscribe({
			next: batches => {
				let i = 0;
				for (const batch of batches) {
					for (const b of batch.results) {
						this.totalOfWeight.push(b.qtyBatchGross || 0);
						this.timeInStockGraphConfig[i].value = this.totalOfWeight[i];
						i++;
					}
				}
				this.isLoading = false;
			},
			error: (err: unknown) => {
				console.error('error requesting batches datacenter', err);
				this.isLoading = false;
			}
		});
	}

	getBatchWithDaysInstock(field: string, from: string, to: string) {
		return this.batchService.getBatches(
			['qtyBatchGross'],
			luceneToString(this.createDateRangeFilter(field, from, to)),
			1,
			250,
			new OrderBy('qtyBatchGross', OrderByDirection.ASC),
			true
		);
	}

	createDateRangeFilter(field: string, from: string, to: string) {
		const left: RangeExpression = {
			field,
			term_min: from,
			term_max: to,
			inclusive: 'both'
		};
		const right: FieldExpression = {
			field: 'statusGroup',
			term: '2',
			quoted: true
		};
		return <const> {
			left,
			operator: 'AND',
			right,
			parenthesized: true
		};
	}

	ngOnDestroy() {
		if (this.orderSub) {
			this.orderSub.unsubscribe();
		}
	}

	async viewDetails() {
		await this.viewDetailsFilter();
	}

	async barLinkWithFilter(from: string | number, to: string | number) {
		if (this.hasEcallOff) {
			await this.router.navigate(['/ecall-off/ecall-off-steps/stepMatPicking'], {
				state: {
					statusGroup: '2',
					daysInStockFrom: from.toString(),
					daysInStockTo: to.toString()
				}
			});
			return;
		}

		const defaultView = this.defaultConfiguration.getDefaultView('batch');
		const filtersList: DatacenterFilterType[] = [];
		const statusGroupFilter: DatacenterFilterType = {
			id: 'statusGroup',
			values: [
				{
					code: '2',
					translation: 'Available - Ready To Ship',
				}
			]
		};

		const numberOfDaysInStockFilter = {
			id: 'daysInStock',
			values: [
				{
					from: from.toString(),
					to: to.toString()
				}
			]
		};

		filtersList.push(statusGroupFilter, numberOfDaysInStockFilter);
		defaultView.filterState = filtersList;

		await this.router.navigate(['/order-tracking/batch/datacenter'], {
			relativeTo: this.route,
			state: { ...this.previousState, view: defaultView }
		});
	}

	async viewDetailsFilter() {
		if (this.hasEcallOff) {
			await this.router.navigate(['/ecall-off/ecall-off-steps/stepMatPicking'], {
				state: { statusGroup: '2' }
			});
			return;
		}

		const defaultView = this.defaultConfiguration.getDefaultView('batch');
		const filtersList: DatacenterFilterType[] = [];
		const statusGroupFilter: DatacenterFilterType = {
			id: 'statusGroup',
			values: [
				{
					code: '2',
					translation: 'Available - Ready To Ship',
				}
			]
		};
		filtersList.push(statusGroupFilter);
		defaultView.filterState = filtersList;

		await this.router.navigate(['/order-tracking/batch/datacenter'], {
			relativeTo: this.route,
			state: { ...this.previousState, view: defaultView }
		});
	}

	async linkTo(barIndex: number) {
		switch (barIndex) {
		case 0:
			await this.barLinkWithFilter('0', '30');
			break;
		case 1:
			await this.barLinkWithFilter('31', '60');
			break;
		case 2:
			await this.barLinkWithFilter('61', '90');
			break;
		case 3:
			await this.barLinkWithFilter('91', '');
			break;
		}
	}
}
